/**
 * @file presets.ts
 * @description Четыре стартовых шаблона темы кабинета (phase19.31, SC2, SC12).
 *
 * Единственный источник заводских палитр в коде. В базу они попадают
 * data-миграцией со стабильными id (эталон —
 * apps/api/prisma/migrations/20261002130000_phase19_31_cabinet_themes);
 * тест-страж в apps/api сверяет базу с этим файлом, поэтому любая правка
 * палитры здесь требует data-миграции:
 *  - «Commora» (системная) — UPDATE без условий;
 *  - остальные — WHERE "customizedAt" IS NULL AND "presetVersion" < N.
 *
 * История версий: 1 — стартовые палитры (20261002130000); 2 — «Commora» и
 * «Светлая»: вторичный и приглушённый текст (on-surface-variant,
 * on-surface-muted) #64748b → #566476, SC65 (20261003120400); +1 у всех
 * четырёх (Commora 3, Светлая 3, Природная 2, Ночная 2) — необязательный ключ
 * `gradient-warm`, тёплый тон фона «шёлковые ленты», SC66 (20261003120700).
 *
 * Необязательный ключ добавляется data-миграцией иначе, чем правка значения:
 * ключ дописывается в КАЖДЫЙ шаблон, где его нет (и в правленный редактором —
 * его цвета администратора не меняются, а дописанный ключ на контраст текста
 * не влияет), версия поднимается только у заводских, не правленных редактором
 * (и всегда у системного).
 *
 * «Commora» ещё и запасная тема: сервер отдаёт её палитру, если палитра
 * шаблона в базе не прошла проверку, а кабинет — если не дождался ответа.
 */

import type { CabinetFullPalette, CabinetThemeScheme } from "./keys.js";

/** Заводской шаблон: всё, что миграция кладёт в строку `CabinetTheme`. */
export interface CabinetThemePreset {
  /** Стабильный id строки (на `cabtheme_commora` ссылается DEFAULT `Shop`). */
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly scheme: CabinetThemeScheme;
  readonly isSystem: boolean;
  readonly isDefault: boolean;
  readonly sortOrder: number;
  /** Версия заводской палитры; растёт вместе с data-миграцией обновления. */
  readonly presetVersion: number;
  /** Полная палитра: заводской шаблон обязан задавать и необязательные ключи. */
  readonly tokens: CabinetFullPalette;
}

/** Ключ системного шаблона — запасной темы при ошибке палитры (SC10). */
export const CABINET_FALLBACK_THEME_KEY = "commora";

const COMMORA: CabinetThemePreset = {
  id: "cabtheme_commora",
  key: "commora",
  name: "Commora",
  description: "Тёмная панель и синие действия — деловая тема платформы",
  scheme: "LIGHT",
  isSystem: true,
  isDefault: true,
  sortOrder: 10,
  // 2 — SC65: вторичный и приглушённый текст #566476 (миграция phase19_31_cabinet_theme_secondary_text).
  // 3 — SC66: тёплый тон фона gradient-warm (миграция phase19_31_cabinet_theme_gradient_warm).
  presetVersion: 3,
  tokens: {
    background: "#f8fafc",
    "surface-low": "#f1f5f9",
    "surface-lowest": "#ffffff",
    "surface-bright": "#ffffff",
    input: "#ffffff",
    "on-surface": "#0f172a",
    // SC65 (presetVersion 2): было #64748b — затемнено под фон-волну кабинета.
    "on-surface-variant": "#566476",
    // SC65 (presetVersion 2): было #64748b — тот же серый, лежит на фоне-волне.
    "on-surface-muted": "#566476",
    "on-surface-strong": "#334155",
    primary: "#2563eb",
    "primary-hover": "#1d4ed8",
    "primary-pressed": "#1e40af",
    "on-primary": "#ffffff",
    link: "#2563eb",
    ring: "#3b82f6",
    "gradient-start": "#2563eb",
    "gradient-end": "#7c3aed",
    // SC66 (presetVersion 3): тёплый тон декоративного фона, на нём нет текста.
    "gradient-warm": "#ec4899",
    accent: "#eff6ff",
    "accent-foreground": "#1d4ed8",
    "soft-hover": "#dbeafe",
    "soft-pressed": "#bfdbfe",
    "soft-pressed-fg": "#1e40af",
    border: "#e2e8f0",
    "field-border": "#cbd5e1",
    "field-border-hover": "#94a3b8",
    "field-border-focus": "#3b82f6",
    "table-head-bg": "#f8fafc",
    "row-hover": "#f1f5f9",
    "row-selected": "#eff6ff",
    "row-marker": "#3b82f6",
    sidebar: "#0b1020",
    "sidebar-foreground": "#cbd5e1",
    "sidebar-muted": "#94a3b8",
    "sidebar-strong": "#ffffff",
    "sidebar-accent": "#2563eb",
    "sidebar-accent-foreground": "#ffffff",
    "sidebar-hover": "#ffffff0f",
    "sidebar-hover-foreground": "#ffffff",
    "sidebar-border": "#ffffff14",
    "sidebar-edge": "#00000000",
    "sidebar-ring": "#93c5fd",
    "sidebar-marker": "#00000000",
    "tooltip-bg": "#0b1020",
    "tooltip-fg": "#ffffff",
  },
};

const LIGHT: CabinetThemePreset = {
  id: "cabtheme_light",
  key: "light",
  name: "Светлая",
  description: "Белая панель и синие действия — спокойно, как в офисных программах",
  scheme: "LIGHT",
  isSystem: false,
  isDefault: false,
  sortOrder: 20,
  // 2 — SC65: вторичный и приглушённый текст #566476 (миграция phase19_31_cabinet_theme_secondary_text).
  // 3 — SC66: тёплый тон фона gradient-warm (миграция phase19_31_cabinet_theme_gradient_warm).
  presetVersion: 3,
  tokens: {
    background: "#f8fafc",
    "surface-low": "#f1f5f9",
    "surface-lowest": "#ffffff",
    "surface-bright": "#ffffff",
    input: "#ffffff",
    "on-surface": "#0f172a",
    // SC65 (presetVersion 2): было #64748b — затемнено под фон-волну кабинета.
    "on-surface-variant": "#566476",
    // SC65 (presetVersion 2): было #64748b — тот же серый, лежит на фоне-волне.
    "on-surface-muted": "#566476",
    "on-surface-strong": "#334155",
    primary: "#2563eb",
    "primary-hover": "#1d4ed8",
    "primary-pressed": "#1e40af",
    "on-primary": "#ffffff",
    link: "#2563eb",
    ring: "#3b82f6",
    "gradient-start": "#2563eb",
    "gradient-end": "#7c3aed",
    // SC66 (presetVersion 3): тёплый тон декоративного фона, на нём нет текста.
    "gradient-warm": "#ec4899",
    accent: "#eff6ff",
    "accent-foreground": "#1d4ed8",
    "soft-hover": "#dbeafe",
    "soft-pressed": "#bfdbfe",
    "soft-pressed-fg": "#1e40af",
    border: "#e2e8f0",
    "field-border": "#cbd5e1",
    "field-border-hover": "#94a3b8",
    "field-border-focus": "#3b82f6",
    "table-head-bg": "#f8fafc",
    "row-hover": "#f1f5f9",
    "row-selected": "#eff6ff",
    "row-marker": "#3b82f6",
    sidebar: "#ffffff",
    "sidebar-foreground": "#334155",
    "sidebar-muted": "#64748b",
    "sidebar-strong": "#0f172a",
    "sidebar-accent": "#eff6ff",
    "sidebar-accent-foreground": "#1d4ed8",
    "sidebar-hover": "#f1f5f9",
    "sidebar-hover-foreground": "#0f172a",
    "sidebar-border": "#e2e8f0",
    "sidebar-edge": "#e2e8f0",
    "sidebar-ring": "#3b82f6",
    "sidebar-marker": "#2563eb",
    "tooltip-bg": "#0f172a",
    "tooltip-fg": "#ffffff",
  },
};

const NATURE: CabinetThemePreset = {
  id: "cabtheme_nature",
  key: "nature",
  name: "Природная",
  description: "Тёплые тона: кремовый фон, оливковые кнопки, тёмно-оливковая панель",
  scheme: "LIGHT",
  isSystem: false,
  isDefault: false,
  sortOrder: 30,
  // 2 — SC66: тёплый тон фона gradient-warm (миграция phase19_31_cabinet_theme_gradient_warm).
  presetVersion: 2,
  tokens: {
    background: "#faf7f0",
    "surface-low": "#f7f3e9",
    "surface-lowest": "#ffffff",
    "surface-bright": "#ffffff",
    input: "#ffffff",
    "on-surface": "#2a2f1a",
    "on-surface-variant": "#6b6253",
    "on-surface-muted": "#6b6253",
    "on-surface-strong": "#454a33",
    primary: "#5a6b36",
    "primary-hover": "#4b5a2c",
    "primary-pressed": "#3d4b1f",
    "on-primary": "#ffffff",
    link: "#5a6b36",
    ring: "#7a8b46",
    "gradient-start": "#5a6b36",
    "gradient-end": "#8a5a2b",
    // SC66 (presetVersion 2): тёплый тон декоративного фона, на нём нет текста.
    "gradient-warm": "#d98a3d",
    accent: "#fbecce",
    "accent-foreground": "#4b5a2c",
    "soft-hover": "#f6e0b5",
    "soft-pressed": "#efd49c",
    "soft-pressed-fg": "#3d4b1f",
    border: "#e9e2d0",
    "field-border": "#d6ccb4",
    "field-border-hover": "#a99d82",
    "field-border-focus": "#5a6b36",
    "table-head-bg": "#faf7f0",
    "row-hover": "#f7f3e9",
    "row-selected": "#fcf3df",
    "row-marker": "#5a6b36",
    sidebar: "#2a2f1a",
    "sidebar-foreground": "#e7e1cf",
    "sidebar-muted": "#b5ac95",
    "sidebar-strong": "#ffffff",
    "sidebar-accent": "#fbecce",
    "sidebar-accent-foreground": "#2a2f1a",
    "sidebar-hover": "#ffffff14",
    "sidebar-hover-foreground": "#ffffff",
    "sidebar-border": "#ffffff14",
    "sidebar-edge": "#00000000",
    "sidebar-ring": "#e0c67f",
    "sidebar-marker": "#00000000",
    "tooltip-bg": "#2a2f1a",
    "tooltip-fg": "#ffffff",
  },
};

const NIGHT: CabinetThemePreset = {
  id: "cabtheme_night",
  key: "night",
  name: "Ночная",
  description: "Тёмный экран для работы вечером и ночью, синие действия",
  scheme: "DARK",
  isSystem: false,
  isDefault: false,
  sortOrder: 40,
  // 2 — SC66: тёплый тон фона gradient-warm (миграция phase19_31_cabinet_theme_gradient_warm).
  presetVersion: 2,
  tokens: {
    background: "#0b1020",
    "surface-low": "#101728",
    "surface-lowest": "#131a2c",
    "surface-bright": "#1a2236",
    input: "#0f1626",
    "on-surface": "#e2e8f0",
    "on-surface-variant": "#94a3b8",
    "on-surface-muted": "#94a3b8",
    "on-surface-strong": "#f1f5f9",
    primary: "#2563eb",
    "primary-hover": "#1d4ed8",
    "primary-pressed": "#1e40af",
    "on-primary": "#ffffff",
    link: "#60a5fa",
    ring: "#60a5fa",
    "gradient-start": "#2563eb",
    "gradient-end": "#7c3aed",
    // SC66 (presetVersion 2): тёплый тон декоративного фона, на нём нет текста.
    "gradient-warm": "#f472b6",
    accent: "#172554",
    "accent-foreground": "#93c5fd",
    "soft-hover": "#1e3a8a",
    "soft-pressed": "#1e40af",
    "soft-pressed-fg": "#dbeafe",
    border: "#26304a",
    "field-border": "#64748b",
    "field-border-hover": "#94a3b8",
    "field-border-focus": "#3b82f6",
    "table-head-bg": "#101728",
    "row-hover": "#1a2236",
    "row-selected": "#172554",
    "row-marker": "#3b82f6",
    sidebar: "#070b16",
    "sidebar-foreground": "#cbd5e1",
    "sidebar-muted": "#94a3b8",
    "sidebar-strong": "#ffffff",
    "sidebar-accent": "#2563eb",
    "sidebar-accent-foreground": "#ffffff",
    "sidebar-hover": "#ffffff0f",
    "sidebar-hover-foreground": "#ffffff",
    "sidebar-border": "#ffffff14",
    "sidebar-edge": "#1e293b",
    "sidebar-ring": "#93c5fd",
    "sidebar-marker": "#00000000",
    "tooltip-bg": "#e2e8f0",
    "tooltip-fg": "#0f172a",
  },
};

/** Все заводские шаблоны в порядке показа (`sortOrder`). */
export const CABINET_THEME_PRESETS: readonly CabinetThemePreset[] = [COMMORA, LIGHT, NATURE, NIGHT];

/** Запасной шаблон — «Commora». */
export const CABINET_FALLBACK_PRESET: CabinetThemePreset = COMMORA;

/** Заводской шаблон по ключу; `undefined` — шаблон создан на платформе. */
export function findCabinetThemePreset(key: string): CabinetThemePreset | undefined {
  return CABINET_THEME_PRESETS.find((preset) => preset.key === key);
}
